import { FC, useState, useEffect } from 'react';
import { Modal, Button, Tag, Checkbox } from 'antd';
import { MailOutlined, SendOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

export interface EmailRecipient {
  email: string;
  name: string;
  role?: string;
  isClient?: boolean;
}

interface SendEmailConfirmationModalProps {
  open: boolean;
  recipients: EmailRecipient[];
  subject: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

export const SendEmailConfirmationModal: FC<SendEmailConfirmationModalProps> = ({
  open,
  recipients,
  subject,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const [confirmed, setConfirmed] = useState(false);

  // Reset confirmation when modal opens/closes
  useEffect(() => {
    if (!open) {
      setConfirmed(false);
    }
  }, [open]);

  // Separate TO recipients from CC/BCC for display
  const toRecipients = recipients.filter(r => r.role !== 'CC' && r.role !== 'BCC');
  const ccRecipients = recipients.filter(r => r.role === 'CC');
  const bccRecipients = recipients.filter(r => r.role === 'BCC');

  return (
    <Modal
      open={open}
      title={
        <div className="flex items-center gap-2">
          <ExclamationCircleOutlined className="text-amber-500" />
          <span>Confirm Email Recipients</span>
        </div>
      }
      footer={null}
      onCancel={onCancel}
      width={600}
      closable={!loading}
      maskClosable={!loading}
    >
      <div className="py-4">
        {/* Warning Banner */}
        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded">
          <p className="text-sm text-amber-800 font-medium">
            Please carefully review the email addresses below before sending.
          </p>
        </div>

        {/* Subject */}
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded">
          <p className="text-sm text-gray-700">
            <strong>Subject:</strong> {subject}
          </p>
        </div>

        {/* TO Recipients - Most prominent */}
        <div className="mb-4">
          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2 text-gray-800">
            <MailOutlined /> TO: {toRecipients.length} recipient{toRecipients.length !== 1 ? 's' : ''}
          </h4>
          <div className="max-h-48 overflow-y-auto border-2 border-blue-200 rounded bg-blue-50">
            {toRecipients.length === 0 ? (
              <div className="p-3 text-red-600 text-sm font-medium">
                No TO recipients selected!
              </div>
            ) : (
              <div className="divide-y divide-blue-100">
                {toRecipients.map((recipient, index) => {
                  const isClient = recipient.isClient || recipient.role?.toLowerCase().includes('client');
                  return (
                    <div
                      key={index}
                      className={`p-3 flex items-center justify-between ${isClient ? 'bg-red-50' : ''}`}
                    >
                      <div className="flex-1">
                        <div className={`font-medium text-sm ${isClient ? 'text-red-900' : 'text-gray-900'}`}>
                          {recipient.name}
                        </div>
                        <div className={`text-sm font-mono ${isClient ? 'text-red-700' : 'text-blue-700'}`}>
                          {recipient.email}
                        </div>
                      </div>
                      {recipient.role && (
                        <Tag color={isClient ? 'red' : 'blue'} className="text-xs">
                          {isClient ? `⚠ ${recipient.role}` : recipient.role}
                        </Tag>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* CC Recipients */}
        {ccRecipients.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2 text-gray-600">
              CC: {ccRecipients.length} recipient{ccRecipients.length !== 1 ? 's' : ''}
            </h4>
            <div className="max-h-32 overflow-y-auto border border-gray-200 rounded bg-gray-50">
              <div className="divide-y divide-gray-100">
                {ccRecipients.map((recipient, index) => {
                  const isClient = recipient.isClient;
                  return (
                    <div
                      key={index}
                      className={`p-2 flex items-center justify-between ${isClient ? 'bg-red-50' : ''}`}
                    >
                      <div className="flex-1">
                        <div className={`font-medium text-sm ${isClient ? 'text-red-900' : 'text-gray-900'}`}>
                          {recipient.name}
                        </div>
                        <div className={`text-sm font-mono ${isClient ? 'text-red-700' : 'text-gray-700'}`}>
                          {recipient.email}
                        </div>
                      </div>
                      {isClient && (
                        <Tag color="red" className="text-xs">
                          ⚠ Client
                        </Tag>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* BCC Recipients */}
        {bccRecipients.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2 text-gray-600">
              BCC: {bccRecipients.length} recipient{bccRecipients.length !== 1 ? 's' : ''}
            </h4>
            <div className="max-h-32 overflow-y-auto border border-gray-200 rounded bg-gray-50">
              <div className="divide-y divide-gray-100">
                {bccRecipients.map((recipient, index) => {
                  const isClient = recipient.isClient;
                  return (
                    <div
                      key={index}
                      className={`p-2 flex items-center justify-between ${isClient ? 'bg-red-50' : ''}`}
                    >
                      <div className="flex-1">
                        <div className={`font-medium text-sm ${isClient ? 'text-red-900' : 'text-gray-900'}`}>
                          {recipient.name}
                        </div>
                        <div className={`text-sm font-mono ${isClient ? 'text-red-700' : 'text-gray-700'}`}>
                          {recipient.email}
                        </div>
                      </div>
                      {isClient && (
                        <Tag color="red" className="text-xs">
                          ⚠ Client
                        </Tag>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Double Confirmation Checkbox */}
        <div className="mb-4 p-4 bg-gray-100 border border-gray-300 rounded">
          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            disabled={loading}
          >
            <span className="text-sm font-medium text-gray-800">
              I have verified that the email addresses above are correct and I want to send this email
            </span>
          </Checkbox>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2">
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="primary"
            danger
            icon={<SendOutlined />}
            onClick={onConfirm}
            loading={loading}
            disabled={!confirmed || toRecipients.length === 0}
          >
            Confirm & Send Email
          </Button>
        </div>
      </div>
    </Modal>
  );
};
